import type { ReactNode } from "react";
import { Link } from "react-router";

import { Etiqueta } from "@/components/ui/etiqueta";
import { FiltroSegmentado } from "@/components/ui/filtro-segmentado";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  agruparSecciones,
  ETIQUETA_ESTADO_DE_REDACCION,
  necesitaRevision,
  tonoDeLaSeccion,
  type FiltroDeSecciones,
  type NumeroDeSeccion,
  type SeccionDelInforme,
  type VistaDeSecciones,
} from "@/lib/redaccion";
import { cn } from "@/lib/utils";

/** Dónde se confirma el modo de producción de un ítem. */
export const RUTA_DEL_MAESTRO = "/admin/contenidos/maestro";

interface ArbolDeSeccionesProps {
  /** Las secciones ya filtradas, en el orden de la plantilla. */
  secciones: SeccionDelInforme[];
  /** TODAS las secciones: los contadores de los filtros no cambian al filtrar. */
  todas: SeccionDelInforme[];
  /** Numeración del documento de cada sección, calculada sobre la lista COMPLETA. */
  numeros: Map<number, NumeroDeSeccion>;
  seleccionadaId: number | null;
  vista: VistaDeSecciones;
  onElegir: (itemId: number) => void;
  onVista: (vista: VistaDeSecciones) => void;
  /** Lo que va arriba del árbol, dentro de la tarjeta: redacción en bloque y su avance. */
  cabecera?: ReactNode;
}

type OpcionDelFiltro = FiltroDeSecciones | "todas";

/**
 * El árbol del informe: capítulo › sección, con el estado de cada una. Es la
 * navegación de la fase 3 — elegir una abre su panel a la derecha.
 *
 * El filtro es el segmentado del diseño (Por revisar · Redactadas por IA) más
 * «Todas», que es el estado sin filtro: el diseño no lo dibuja, pero sin él no
 * habría cómo volver a la lista completa. «Ver todo el informe» suma lo que se
 * produce sin IA para quien quiere ver el informe entero.
 */
export function ArbolDeSecciones({
  secciones,
  todas,
  numeros,
  seleccionadaId,
  vista,
  onElegir,
  onVista,
  cabecera,
}: ArbolDeSeccionesProps) {
  const porRevisar = todas.filter(necesitaRevision).length;
  const redactadasPorIa = todas.filter((s) => s.se_redacta_con_ia).length;

  return (
    <div className="bg-card flex min-w-0 flex-col gap-3 rounded-[1.25rem] border-[1.5px] p-3">
      {cabecera}

      <FiltroSegmentado<OpcionDelFiltro>
        aria-label="Filtrar las secciones"
        className="w-full [&_[data-slot=filtro-segmentado-opcion]]:flex-1 [&_[data-slot=filtro-segmentado-opcion]]:justify-center [&_[data-slot=filtro-segmentado-opcion]]:px-1.5"
        valor={vista.filtro ?? "todas"}
        onCambio={(opcion) => onVista({ ...vista, filtro: opcion === "todas" ? null : opcion })}
        opciones={[
          { valor: "todas", etiqueta: "Todas" },
          { valor: "por_revisar", etiqueta: "Por revisar", cantidad: porRevisar },
          { valor: "redactadas_ia", etiqueta: "Redactadas por IA", cantidad: redactadasPorIa },
        ]}
      />

      <div className="flex items-center gap-2.5 px-0.5">
        <Switch
          id="ver-todo-el-informe"
          checked={vista.verTodo}
          onCheckedChange={(verTodo) => onVista({ ...vista, verTodo })}
        />
        <Label htmlFor="ver-todo-el-informe" className="text-ink text-xs font-bold">
          Ver todo el informe
        </Label>
        <span className="text-ink-medio ml-auto font-mono text-[0.6875rem] font-semibold">
          {secciones.length} {secciones.length === 1 ? "ítem" : "ítems"}
        </span>
      </div>

      <nav aria-label="Secciones del informe" className="flex max-h-[65vh] flex-col gap-2 overflow-y-auto">
        {secciones.length === 0 ? (
          <p className="text-meta text-ink-medio px-1 py-4 text-center">Ninguna sección coincide con el filtro.</p>
        ) : (
          agruparSecciones(secciones).map((grupo) => {
            const titulo = numeros.get(grupo.subgrupos[0].secciones[0].item_id)?.capitulo ?? grupo.grupo ?? "Sin capítulo";

            return (
              <section key={grupo.grupo ?? "sin-capitulo"} className="flex flex-col gap-0.5">
                <h3 className="rotulo-seccion px-1.5 pt-2 pb-1">{titulo}</h3>

                {grupo.subgrupos.map((sub) => (
                  <div key={sub.subgrupo ?? "sin-subcapitulo"} className="flex flex-col gap-0.5">
                    {sub.subgrupo ? (
                      <h4 className="text-ink-hint px-1.5 text-[0.6875rem] font-semibold">{sub.subgrupo}</h4>
                    ) : null}

                    {sub.secciones.map((s) => (
                      <FilaDeSeccion
                        key={s.item_id}
                        seccion={s}
                        numero={numeros.get(s.item_id)?.id ?? null}
                        seleccionada={s.item_id === seleccionadaId}
                        onElegir={onElegir}
                      />
                    ))}
                  </div>
                ))}
              </section>
            );
          })
        )}
      </nav>
    </div>
  );
}

function FilaDeSeccion({
  seccion,
  numero,
  seleccionada,
  onElegir,
}: {
  seccion: SeccionDelInforme;
  numero: string | null;
  seleccionada: boolean;
  onElegir: (itemId: number) => void;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        aria-current={seleccionada ? "true" : undefined}
        onClick={() => onElegir(seccion.item_id)}
        className={cn(
          "flex min-h-[2.375rem] items-center gap-2 rounded-[0.625rem] border-[1.5px] px-2 py-1.5 text-left transition-colors",
          seleccionada ? "bg-divisor border-input" : "hover:bg-superficie-tenue border-transparent",
        )}
      >
        <span className="text-ink-medio w-[1.625rem] shrink-0 font-mono text-[0.65625rem] font-bold">{numero ?? ""}</span>
        <span
          className={cn(
            "text-ink min-w-0 flex-1 truncate text-[0.78125rem]",
            seccion.se_redacta_con_ia ? "font-bold" : "font-semibold",
          )}
          title={seccion.nombre}
        >
          {seccion.nombre}
        </span>
        <InsigniaDeLaSeccion seccion={seccion} />
      </button>

      {!seccion.modo_confirmado ? (
        <Link to={RUTA_DEL_MAESTRO} className="text-success-soft-foreground pl-9 text-xs font-semibold hover:underline">
          Confirmar el modo en el maestro
        </Link>
      ) : null}
    </div>
  );
}

/** El modo para lo que no redacta la IA; el estado (uno solo, sin apilar) para lo que sí. */
function InsigniaDeLaSeccion({ seccion }: { seccion: SeccionDelInforme }) {
  if (!seccion.modo_confirmado) {
    return (
      <Etiqueta size="sm" tono="tenue" className="border border-dashed border-current">
        Modo sin confirmar
      </Etiqueta>
    );
  }

  const r = seccion.redaccion;
  if (!seccion.se_redacta_con_ia || r === null) {
    return (
      <Etiqueta size="sm" tono="tenue">
        {seccion.modo_etiqueta ?? "—"}
      </Etiqueta>
    );
  }

  // Una validada que quedó desactualizada es OTRO estado en el diseño (naranja),
  // no una validada con una segunda etiqueta al lado.
  const desactualizada = r.estado === "validada" && r.desactualizada;

  return (
    <Etiqueta size="sm" tono={tonoDeLaSeccion(seccion)}>
      {desactualizada ? "Desactualizada" : ETIQUETA_ESTADO_DE_REDACCION[r.estado]}
    </Etiqueta>
  );
}
